/**
 * Which school a farm row's chip should name.
 *
 * `src/data/farm-log.json` is a TRANSCRIPTION. Its `school` values were copied from the league
 * sheet and the draft archives character for character — that was verified by diffing it against
 * `data/farm-log-raw.json` and finding zero differences, and it is the reason the log can be
 * trusted as evidence of what the league actually recorded. So the fixes below live HERE and not
 * in the data file: correcting "Lousiville" in the JSON would silently destroy the proof that the
 * commissioner typed it that way.
 *
 * Two kinds of correction, and the difference matters:
 *
 *   ALIASES are the same school under another string — a typo or an abbreviation. Nothing was
 *   looked up; the row already named the school.
 *
 *   FIXUPS fill a school the sheet left EMPTY. Six 2024 rows have a player and a bid and no
 *   school at all. Each one below carries the source it was resolved from, because a value nobody
 *   can trace is a value somebody invented. All six resolve to schools already in the chip table.
 *
 * Signed off by Tim on 21 Aug 2026: "if there's a missing school, do research to figure out what
 * school it should be / FSU = Florida State".
 */

/** Same school, different string. No research involved. */
export const SCHOOL_ALIASES: Readonly<Record<string, string>> = {
  // Roydell Williams, Ryan's 2023 pick. Tim's call, 21 Aug 2026.
  FSU: 'Florida State',
  // Isaac Brown, Matt's 2025 pick. A transposition in the sheet; the row's own `college` field
  // spells it correctly, which is what makes this an alias rather than a guess.
  Lousiville: 'Louisville',
};

/**
 * Rows the sheet left blank, resolved by research. Keyed by player name — farm rows are one per
 * player, and the six below are all 2024 auction wins.
 */
export const SCHOOL_FIXUPS: Readonly<Record<string, string>> = {
  // Already carried in the row's `college` field; only `school` was blank.
  'Jeremiah Smith': 'Ohio State',
  // nfl.com and raiders.com, 2025 draft coverage: Boise State, 6th overall to Las Vegas.
  'Ashton Jeanty': 'Boise State',
  // nfl.com and tennesseetitans.com, 2025 draft: Miami QB, 1st overall to Tennessee.
  'Cam Ward': 'Miami',
  // University of Arizona Athletics, 2025 draft: Arizona WR, 8th overall.
  'Tetairoa McMillan': 'Arizona',
  // ocolly.com and cbssports.com, Dec 2024 declaration: Oklahoma State RB.
  'Ollie Gordon II': 'Oklahoma State',
  // fantasypros/nfldraftbuzz 2025 profiles: Missouri WR, 39th overall.
  'Luther Burden III': 'Missouri',
};

export interface HasSchool {
  player: string;
  school?: string;
  college?: string;
}

/**
 * The school to draw, or null when the row genuinely has none.
 *
 * Null is a real answer, not a failure: a PASSED turn has no player and no school, and the design
 * draws it as a half-height rule with no chips at all. What must never happen is a non-null school
 * that has no chip — `CollegeChip` throws on that, and `tests/collegeChips.test.ts` walks all 141
 * rows through this function to prove it cannot.
 */
export function schoolOf(row: HasSchool): string | null {
  const raw = (row.school ?? '').trim() || (row.college ?? '').trim();
  if (!raw) return SCHOOL_FIXUPS[row.player] ?? null;
  return SCHOOL_ALIASES[raw] ?? raw;
}
